/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Icon } from '@/components/Icon';
import { Button } from '@/design-system';
import { useUiStore } from '@/stores/uiStore';
import { hasVisibleStop, isTourSeen, markTourSeen, tourForPath, type PageTour } from './tourSteps';
import './tour.css';

/** How long after the page changes the invitation looks for its tour targets again. */
export const INVITE_POLL_MS = 400;

type InvitedTour = PageTour & Required<Pick<PageTour, 'invite'>>;

/**
 * The once-per-user invitation to the page's tour ("New to ...? Take a 1-minute tour"). Small,
 * dismissible and bottom-left beside the rail, clear of the feedback button. It never starts the
 * tour by itself: Start does, and Start or dismiss both mean it is not offered again.
 */
export function TourInvite() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const tourOpen = useUiStore((s) => s.tourOpen);
  const startTour = useUiStore((s) => s.startTour);
  const [found, setFound] = useState<{ path: string; tour: InvitedTour } | null>(null);
  // Kept here as well as in storage, so an answer sticks even when storage cannot take it.
  const [answered, setAnswered] = useState<ReadonlySet<PageTour['id']>>(() => new Set());

  /*
   * Look for this page's tour once its targets are on screen, and again whenever the page changes
   * under the same path: targets render late (lazy chunks, data), and a view can open in place
   * (the DTC workspace inside On-Line and Off-Line).
   */
  useEffect(() => {
    let timer = 0;
    const look = () => {
      timer = 0;
      const tour = tourForPath(pathname);
      const invite = tour?.invite;
      const offer: InvitedTour | null =
        tour && invite && !isTourSeen(tour.id) && hasVisibleStop(tour) ? { ...tour, invite } : null;
      setFound((prev) => {
        if (!offer) return null;
        return prev?.path === pathname && prev.tour.id === offer.id
          ? prev
          : { path: pathname, tour: offer };
      });
    };
    const schedule = () => {
      if (!timer) timer = window.setTimeout(look, INVITE_POLL_MS);
    };
    schedule();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [pathname]);

  const tour = found?.path === pathname ? found.tour : null;
  if (!tour || tourOpen || answered.has(tour.id) || isTourSeen(tour.id)) return null;

  const answer = () => {
    markTourSeen(tour.id);
    setAnswered((prev) => new Set(prev).add(tour.id));
  };

  return (
    <div className="tour-invite" role="dialog" aria-label={t(tour.label)}>
      <Icon name="explore" size={20} />
      <div className="tour-invite-text">
        <strong>{t(tour.invite.title)}</strong>
        <span>{t(tour.invite.body)}</span>
      </div>
      <Button
        type="button"
        size="sm"
        onClick={() => {
          answer();
          startTour();
        }}
      >
        {t('tour.invite.start')}
      </Button>
      <button
        type="button"
        className="iclose"
        onClick={answer}
        aria-label={t('home.hero.dismiss')}
        title={t('home.hero.dismiss')}
      >
        <Icon name="close" size={18} />
      </button>
    </div>
  );
}
